import React, {useCallback, useMemo, useRef, useState} from 'react';
import {
    DropdownHeader,
    DropdownSection,
    DropdownV2,
    Flex,
    Icon,
    Input,
    ListItem,
    PriorityIcon,
    TriggerWrapperV2
} from '@priority-software/priority-style-react';
import {Scrollbar} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useKeyboardNavigation} from '../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {isRTL} from '../../../../Localization';
import {texts} from './texts';
import {ActionIcon} from '../../../../components/ActionIcon';
import {useScroll} from '../../../../hooks/dropdown/useScroll';
import styles from './style.module.scss';
import {ActionButton} from '../ActionButton';

type Props = {
    actions?: priority.netitems.form.IAction[];
    componentClasses?: string;
    disabled?: boolean;
    runAction: (actions: priority.netitems.form.IAction) => void;
    openingSide?: 'leftSide' | 'rightSide';
};

export const ActionsDropdown = ({
    actions,
    componentClasses,
    disabled,
    runAction,
    openingSide
}: Props) => {
    const [actionsData, setActionsData] = useState<priority.netitems.form.IAction[]>(actions);
    const [isOpen, setOpen] = useState(false);

    const inputRef = useRef(null);
    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: actionsData.length,
        pageSize: 1,
        onSelect: () => handleSelect(actionsData[focusedIndex])
    });

    const {itemsRef} = useScroll({items: actionsData, focusedItemIndex: focusedIndex});

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            if (value === '') {
                setActionsData(actions);
                return;
            }
            setActionsData(
                actions.filter(act =>
                    act.Title.toLocaleLowerCase().includes(value.toLocaleLowerCase())
                )
            );
        },
        [actions]
    );

    const handleOpenDropdown = useCallback(() => {
        setActionsData(actions);
        setOpen(prev => {
            !prev && setActionsData(actions);
            return !prev;
        });
    }, [actions]);

    const handleSelect = (action?: priority.netitems.form.IAction) => {
        if (action) {
            setOpen(false);
            runAction(action);
        }
    };

    const handleClearValue = useCallback(() => {
        inputRef.current.value = '';
        setActionsData(actions);
    }, [actions]);

    const onClose = useCallback(() => {
        inputRef.current.value = '';
        setActionsData(actions);
        setOpen(false);
    }, [actions]);

    const renderScrollList = useMemo(
        () => (
            <Scrollbar
                autoHeight
                autoHide={false}
                rtl={isRTL()}
                componentClasses={styles.scroll}
                autoHeightMax={410}
            >
                {actionsData.map((action, idx) => (
                    <ListItem
                        ref={itemsRef.current[idx]}
                        isActive={false}
                        isFocused={idx === focusedIndex}
                        key={action.Ename}
                        componentClasses={styles.dropdownItem}
                        onClick={() => handleSelect(action)}
                    >
                        <ActionIcon type={action.Type as number} rounded iconSize="small" />
                        {action.Title}
                    </ListItem>
                ))}
            </Scrollbar>
        ),
        [actionsData, focusedIndex, disabled]
    );

    return (
        <DropdownV2
            oldStyles
            onKeyDown={handleKeyboardNavigation}
            openingSide={openingSide}
            focusOnOpen
            isOpen={isOpen}
            onClose={onClose}
            componentClasses={cx(styles.wrapperDropdown, componentClasses)}
            trigger={
                <TriggerWrapperV2 isOpen={isOpen}>
                    <ActionButton
                        disabled={disabled}
                        onClick={handleOpenDropdown}
                        componentClasses={cx({[styles.marginXmd]: !isOpen}, styles.actionBtn, {
                            [styles.open]: isOpen
                        })}
                        text={texts.actionsBtnLabel}
                        suffixElement={
                            <Icon
                                icon={PriorityIcon.arrowRight}
                                componentClasses={cx(styles.actionBtnArrow)}
                            />
                        }
                    />
                </TriggerWrapperV2>
            }
            eventTypes={['touchstart', 'mousedown']}
        >
            <DropdownHeader title={texts.actionsBtnLabel} componentClasses={styles.title} />
            <DropdownSection>
                <Input
                    inputRef={inputRef}
                    autoFocus
                    placeholder={texts.actionPlaceholder}
                    componentClasses={styles.input}
                    onChange={handleChange}
                >
                    <Flex inline>
                        {inputRef?.current?.value.length ? (
                            <Icon
                                icon={PriorityIcon.closeSmall}
                                componentClasses={styles.icon}
                                onClick={handleClearValue}
                            />
                        ) : (
                            ''
                        )}
                        <Icon icon={PriorityIcon.searchThin} componentClasses={styles.icon} />
                    </Flex>
                </Input>
            </DropdownSection>

            {actionsData && renderScrollList}
        </DropdownV2>
    );
};
